import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  useLocation,
} from "react-router-dom";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { Nav } from "@/components/Nav";
import { MobileNav } from "@/components/MobileNav";
import { RouteSeo } from "@/components/Seo/RouteSeo";
import { animateMotion, motionEnabled, routeEnterMotion } from "@/lib/motion";
import {
  analyticsEnabled,
  trackPageEngagement,
  trackPageView,
} from "@/lib/analytics";
import {
  appVersionLabel,
  formatReleaseDate,
  RELEASES_PAGE_URL,
} from "@/lib/appVersion";
import { useReleaseDate } from "@/hooks/useReleaseDate";
import { OPENF1_SITE_URL } from "@/constants";
import { useSettings } from "@/stores/settings";
import { AppLogo } from "@/components/AppLogo/AppLogo";
import { HOME_PATH } from "@/utils/url";
const RaceWeekend = lazy(() => import("@/pages/RaceWeekend"));
const Home = lazy(() => import("@/pages/Home"));
const Telemetry = lazy(() => import("@/pages/Telemetry"));
const Standings = lazy(() => import("@/pages/Standings"));
const Schedule = lazy(() => import("@/pages/Schedule"));
const DriverProfile = lazy(() => import("@/pages/DriverProfile"));
const ConstructorProfile = lazy(() => import("@/pages/ConstructorProfile"));
const Settings = lazy(() => import("@/pages/Settings"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const NotFound = lazy(() => import("@/pages/NotFound"));
// Modals are code-split and only mounted while open to keep them (and the
// TrackMap preview inside settings) out of the initial bundle.
const SettingsModal = lazy(() =>
  import("@/components/SettingsModal/SettingsModal").then((m) => ({
    default: m.SettingsModal,
  })),
);
const HowItWorksModal = lazy(() =>
  import("@/components/HowItWorksModal/HowItWorksModal").then((m) => ({
    default: m.HowItWorksModal,
  })),
);

function LazyModals() {
  const isSettingsOpen = useSettings((s) => s.isOpen);
  const isHelpOpen = useSettings((s) => s.isHelpOpen);
  return (
    <Suspense fallback={null}>
      {isSettingsOpen && <SettingsModal />}
      {isHelpOpen && <HowItWorksModal />}
    </Suspense>
  );
}

function RouteFallback() {
  return (
    <div className="app-loading app-loading--route" role="status">
      <div className="app-loading__mark"><AppLogo size={40} /></div>
      <div className="app-loading__title">F1 Replay</div>
      <div className="app-loading__status">Loading view</div>
      <div className="app-loading__lights" aria-hidden="true">
        <span /><span /><span /><span /><span />
      </div>
    </div>
  );
}

function RouteAnalyticsTracker() {
  const location = useLocation();
  // Query params such as `t` and `speed` are updated during replay playback.
  // They change shareable replay state, not the page, so don't emit another
  // page_view or split engagement time for each URL sync.
  const currentPath = location.pathname;
  const activePathRef = useRef(currentPath);
  const activeSinceMsRef = useRef(Date.now());

  const flushEngagement = useCallback(
    (reason: "navigate" | "hidden" | "pagehide" | "unmount") => {
      if (!analyticsEnabled()) return;
      const now = Date.now();
      const durationMs = now - activeSinceMsRef.current;
      trackPageEngagement(activePathRef.current, durationMs, reason);
      activeSinceMsRef.current = now;
    },
    [],
  );

  useEffect(() => {
    if (!analyticsEnabled()) return;
    if (activePathRef.current !== currentPath) {
      flushEngagement("navigate");
      activePathRef.current = currentPath;
    }
    activeSinceMsRef.current = Date.now();
    trackPageView(currentPath);
  }, [currentPath, flushEngagement]);

  useEffect(() => {
    if (!analyticsEnabled()) return;

    function onVisibilityChange() {
      if (document.visibilityState === "hidden") {
        flushEngagement("hidden");
        return;
      }
      activeSinceMsRef.current = Date.now();
    }

    function onPageHide() {
      flushEngagement("pagehide");
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
      flushEngagement("unmount");
    };
  }, [flushEngagement]);

  return null;
}

function RouteMotionShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const shellRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!motionEnabled()) return;
    const animation = animateMotion(shellRef.current, routeEnterMotion());
    return () => {
      animation?.revert();
    };
  }, [location.pathname]);

  return (
    <div ref={shellRef} className="flex flex-col flex-1 min-h-0">
      {children}
    </div>
  );
}

function ReleaseVersionLabel() {
  const releaseDate = formatReleaseDate(useReleaseDate());

  return (
    <span className="inline-flex items-center gap-2">
      <a
        href={RELEASES_PAGE_URL}
        target="_blank"
        rel="noreferrer"
        className="font-mono uppercase tracking-caps text-muted/85 transition-colors hover:text-f1red"
      >
        Version {appVersionLabel}
      </a>
      {releaseDate ? (
        <span className="font-mono text-caption text-muted/70">({releaseDate})</span>
      ) : null}
    </span>
  );
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <RouteSeo />
      <RouteAnalyticsTracker />
      <div className="flex flex-col md:h-[100dvh] md:min-h-[100dvh] md:overflow-hidden">
        <Nav />
        <main className="flex flex-col flex-1 min-h-0 pb-[calc(3rem+env(safe-area-inset-bottom))] md:overflow-hidden md:pb-0">
          <RouteMotionShell>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<RaceWeekend />} />
                <Route path={HOME_PATH} element={<Home />} />
                <Route path="/telemetry" element={<Telemetry />} />
                <Route path="/schedule" element={<Schedule />} />
                <Route path="/standings" element={<Standings />} />
                <Route
                  path="/standings/driver/:driverNumber"
                  element={<DriverProfile />}
                />
                <Route
                  path="/standings/team/:teamName"
                  element={<ConstructorProfile />}
                />
                <Route path="/settings" element={<Settings />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </RouteMotionShell>
        </main>
        <footer className="hidden border-t border-panel bg-track/90 px-3 py-1 text-caption text-muted md:block">
          <div className="mx-auto flex w-full items-center justify-between gap-2.5">
            <div className="flex items-center gap-2.5">
              <ReleaseVersionLabel />
              <span aria-hidden="true" className="text-muted">
                |
              </span>
              <span>
                Data source:{" "}
                <a
                  href={OPENF1_SITE_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="text-f1red/85 transition-colors hover:text-f1red"
                >
                  OpenF1
                </a>
              </span>
            </div>
            <div className="flex items-center gap-2.5">
              <Link
                to="/privacy"
                className="text-f1red/85 transition-colors hover:text-f1red"
              >
                Privacy
              </Link>
              <span aria-hidden="true" className="text-muted">
                |
              </span>
              <Link
                to="/terms"
                className="text-f1red/85 transition-colors hover:text-f1red"
              >
                Terms
              </Link>
              <span aria-hidden="true" className="text-muted">
                |
              </span>
              <a
                href="/.well-known/security.txt"
                className="text-f1red/85 transition-colors hover:text-f1red"
              >
                Security
              </a>
            </div>
          </div>
        </footer>
        <MobileNav />
        <LazyModals />
      </div>
    </BrowserRouter>
  );
}
